import { useAdminSession } from '@/lib/admin-session';
import type { VersionTag } from '@matter_extractor/shared';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * « Dupliquer » from a library screen (refonte modulaire §4.2, issue #116).
 *
 * The gesture is a route of the versioning FACTORY, so the four libraries get
 * it without a line of backend code each - and this hook is the same claim kept
 * on the screen side. Written per route it was twenty identical lines four
 * times over, differing by the API function, the cache key and the address to
 * navigate to; the day the refusal wording or the invalidation changed, one of
 * the four would have been left behind.
 *
 * ## Why it does not seed the copy's detail
 *
 * A LIST has no `detail` key of its own in hand, and seeding one from here
 * would put this hook in the business of knowing each object's two cache
 * prefixes. The copy's editor fetches once, which is what every other
 * navigation to an editor does. (The head of the editor did seed it, holding
 * both the answer and the key; that button is gone since #123, and this hook
 * is now the only way a copy is written from the screen.)
 *
 * ## Why it navigates
 *
 * §4.2 asks for it in as many words: the editor of the copy opens, with its
 * name selected. A copy that merely appeared somewhere in a grid sorted by name
 * would have to be hunted for - and the name it is born with is made to be
 * typed over in the two seconds that follow.
 */

export interface VersionedDuplicationConfig {
  /** The object's own `duplicate` call - `duplicateCriterion` and its three siblings. */
  readonly duplicate: (id: string, tag: VersionTag) => Promise<{ id: string }>;
  /** The first segment of the LIST's cache key: `criteria`, `personas`, ... */
  readonly listKey: string;
  /** Where the copy's editor lives - the route builds the navigation it alone types. */
  readonly onCopy: (id: string) => void;
}

/**
 * The mutation, carrying the CARD as well as the tag.
 *
 * The card and not its id, because the grid greys the one being copied by
 * comparing `variables?.card.id` - the very shape the restoration beside it
 * already uses.
 */
export type VersionedDuplication<Card> = UseMutationResult<
  { id: string },
  Error,
  { card: Card; tag: VersionTag }
>;

export function useVersionedDuplication<Card extends { id: string }>(
  config: VersionedDuplicationConfig,
): VersionedDuplication<Card> {
  const { email } = useAdminSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { card: Card; tag: VersionTag }) =>
      config.duplicate(input.card.id, input.tag),
    onSuccess: (copy) => {
      // One card more, so the list is stale - and the card of the SOURCE is
      // unchanged, which is why nothing else is touched.
      void queryClient.invalidateQueries({ queryKey: [config.listKey, email] });
      config.onCopy(copy.id);
    },
  });
}
